import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { combineLatest, Subscription } from 'rxjs';
import { take, filter, switchMap, map } from 'rxjs/operators';
import { showAnimation } from 'src/animations';
import { DoubleCheckDialogComponent, DoubleCheckDialogComponentInputData } from 'src/app/lib/shared/components/dialog/double-check-dialog/double-check-dialog.component';
import { IssueTypes } from 'src/app/lib/shared/globals';
import { DefaultMail, IssueStatus, IssueTransaction } from '../../../classes';
import { IssueTransactionService } from '../../../services/issue-transaction.service';
import { IssueService } from '../../../services/issue.service';
import { IssueTransactionManagementComponentService } from './issue-transaction-management-component-service';

@Component({
  selector: 'app-issue-transaction-management',
  templateUrl: './issue-transaction-management.component.html',
  styleUrls: ['./issue-transaction-management.component.css'],
  animations: [showAnimation],
  providers: [
    IssueTransactionManagementComponentService
  ]
})
export class IssueTransactionManagementComponent implements OnDestroy, OnInit {

  formGroup: FormGroup;

  currentTransaction: IssueTransaction;
  currentDefaultMailGuids: string[] = [];

  private _subscriptions: Subscription[] = [];

  constructor(
    public issueTransactionManagementComponentService: IssueTransactionManagementComponentService,
    private _issueService: IssueService,
    private _issueTransactionService: IssueTransactionService,
    private _formBuilder: FormBuilder,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar
  ) {
    this.formGroup = this._formBuilder.group({
      issueTransaction: this._formBuilder.group(new IssueTransaction()),
      issueTransactionAttributes: this._formBuilder.control([]),
      issueType: this._formBuilder.control(null),
      initialStatus: this._formBuilder.control([]),
      defaultNotifications: this._formBuilder.control([]),
      defaultMails: this._formBuilder.array([])
    });
  }

  createNew() {
    this.currentTransaction = null;
    this.formGroup.controls['issueTransaction'].patchValue(new IssueTransaction());
    this.formGroup.controls['issueTransactionAttributes'].patchValue([]);
    this.formGroup.controls['initialStatus'].patchValue([]);
    this.formGroup.controls['defaultNotifications'].patchValue([]);
    (this.formGroup.controls['defaultMails'] as FormArray).clear();
    this.formGroup.controls['issueType'].patchValue(null);
    this.formGroup.markAsPristine();
  }

  deleteTransaction(event: MouseEvent, transaction: IssueTransaction) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    var number = Math.floor(1000 + Math.random() * 9000).toFixed(0);
    combineLatest([this._dialog.open(DoubleCheckDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      data: {
        doubleCheckValue: number,
        icon: 'delete_forever',
        mode: 'numeric',
        text: `Möchten Sie die Vorgangstransaktion wirklich löschen? Bitte verwenden Sie die folgende Nummer um fortzufahren: ${number}`,
        title: 'Vorgangstransaktion löschen'
      } as DoubleCheckDialogComponentInputData
    }).afterClosed().pipe(filter((result) => result ? true : false), switchMap(() => this._issueTransactionService.deleteIssueTransaction(transaction.guid))), this.issueTransactionManagementComponentService.issueTransactions$.pipe(take(1))]).subscribe({
      next: ([_, transactions]) => {
        let index = transactions.findIndex(x => x.guid === transaction.guid);
        if (index > -1) transactions.splice(index, 1);
        this.createNew();
        this._snackBar.open(`Die Vorgangstransaktion ${transaction.name} wurde erfolgreich gelöscht.`, 'Ok', {
          duration: 2000
        });
      }
    })
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {

  }

  saveFormStatus() {
    var transaction: IssueTransaction = this.formGroup.controls['issueTransaction'].value;
    combineLatest([this._issueTransactionService[transaction.guid ? 'updateIssueTransaction' : 'createIssueTransaction'](transaction).pipe(
      switchMap((issueTransaction: IssueTransaction) => this._issueTransactionService.setIssueTransactionInitialStatus(issueTransaction.guid, this.initialStatusControl.value).pipe(map(() => issueTransaction))),
      switchMap((issueTransaction: IssueTransaction) => this._issueTransactionService.setIssueTransactionAttributes(issueTransaction.guid, this.issueTransactionAttributesControl.value).pipe(map(() => issueTransaction))),
      switchMap((issueTransaction: IssueTransaction) => this._issueTransactionService.setIssueTransactionDefaultNotifications(issueTransaction.guid, this.defaultNotificationsControl.value).pipe(map(() => issueTransaction))),
      switchMap((issueTransaction: IssueTransaction) => {
        let obs = [];
        let mails: DefaultMail[] = this.defaultMailControl.controls.filter(x => x.dirty).map(x => x.value);
        let deleted = this.currentDefaultMailGuids.filter(x => (this.defaultMailControl.value as DefaultMail[]).map(y => y.mailid).indexOf(x) === -1);
        for(let mailGuid of deleted) obs.push(this._issueTransactionService.deleteDefaultMail(mailGuid));
        for(let mail of mails){
          mail.transactionid = issueTransaction.guid;
          if(mail.mailid) obs.push(this._issueTransactionService.updateDefaultMail(mail));
          else obs.push(this._issueTransactionService.createDefaultMail(mail));
        }
        return combineLatest(obs).pipe(map(() => issueTransaction))
      }),
    ), this.issueTransactionManagementComponentService.issueTransactions$.pipe(take(1))]).subscribe({
      next: ([issueTransaction, issueTransactions]) => {
        let index = issueTransactions.findIndex(x => x.guid === issueTransaction.guid);
        if (index > -1) issueTransactions[index] = issueTransaction;
        else issueTransactions.push(issueTransaction);
        this.setIssueTransaction(issueTransaction);
        this._snackBar.open('Änderungen erfolgreich gespeichert', 'Ok', { duration: 2000 });
      },
      error: () => this._snackBar.open('Fehler beim Speichern', 'Ok', { duration: 2000 })
    });
  }

  setIssueTransaction(issueTransaction: IssueTransaction) {
    this.currentTransaction = issueTransaction;
    (this.formGroup.controls['issueTransaction'] as FormGroup).patchValue(issueTransaction);
    if (issueTransaction && issueTransaction.result) {
      this._issueService.getIssueStatusById(issueTransaction.result).pipe(take(1)).subscribe((issueStatus: IssueStatus) => {
        let result = issueStatus.ischangestate ? IssueTypes.Change : issueStatus.isincidentstate ? IssueTypes.Incident : issueStatus.isprojectstate ? IssueTypes.Project : IssueTypes.Issue;
        this.typeControl.setValue(result);
      });
    } else this.typeControl.setValue(IssueTypes.Issue);
    if (issueTransaction && issueTransaction.guid) {
      this._issueTransactionService.getIssueTransactionInitialStatus(issueTransaction.guid).subscribe((result) => this.initialStatusControl.patchValue(result));
      this._issueTransactionService.getIssueTransactionAttributes(issueTransaction.guid).subscribe((result) => this.issueTransactionAttributesControl.patchValue(result));
      this._issueTransactionService.getTransactionDefaultNotifications(issueTransaction.guid).subscribe((result) => this.defaultNotificationsControl.patchValue(result.map(x => x.guid)));
      this._issueTransactionService.getTransactionDefaultMails(issueTransaction.guid).subscribe((result) => {
        this.currentDefaultMailGuids = result.map(x => x.mailid);
        this.defaultMailControl.clear();
        for (let mail of result){
          this.defaultMailControl.push(this._formBuilder.group({
            'maildefaultnotification': this._formBuilder.control(mail.maildefaultnotification),
            'mailid': this._formBuilder.control(mail.mailid),
            'mailreceiver': this._formBuilder.control(mail.mailreceiver),
            'transactionid': this._formBuilder.control(mail.transactionid)
          }));
        }
        this.formGroup.markAsPristine();
      });
    }
  }

  get defaultMailControl(): FormArray {
    return this.formGroup.controls['defaultMails'] as FormArray;
  }
  get defaultNotificationsControl(): AbstractControl {
    return this.formGroup.controls['defaultNotifications'];
  }
  get initialStatusControl(): AbstractControl {
    return this.formGroup.controls['initialStatus'];
  }
  get issueTransactionAttributesControl(): AbstractControl {
    return this.formGroup.controls['issueTransactionAttributes'];
  }
  get typeControl(): AbstractControl {
    return this.formGroup.controls['issueType'];
  }

}
